import { z } from '@tasama/utils';

export const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const MIN_STAY_DAYS = 10;

// Fri+Sat — Saudi Arabia primary market fallback
const SAUDI_WEEKEND = new Set([5, 6]);

export const getWeekendDays = (locale: string): Set<number> => {
  try {
    // 'ar' alone has no region; assume Saudi Arabia (primary market)
    const normalized = locale.startsWith('ar') ? 'ar-SA' : locale;
    const localeObj = new Intl.Locale(normalized);
    const weekInfo = (
      localeObj as Intl.Locale & { weekInfo?: { weekend: number[] } }
    ).weekInfo;
    if (!weekInfo) return SAUDI_WEEKEND;
    // Intl uses ISO day numbers (1=Mon…7=Sun); convert to JS getDay() via d % 7 (7→0=Sun)
    return new Set(weekInfo.weekend.map((d) => d % 7));
  } catch {
    return SAUDI_WEEKEND;
  }
};

export const createIsWeekend = (locale: string) => {
  const weekend = getWeekendDays(locale);
  return (date: Date): boolean => weekend.has(date.getDay());
};

export const addWorkingDays = (
  fromMs: number,
  days: number,
  isWeekendFn: (_date: Date) => boolean,
  isHolidayFn?: (_date: Date) => boolean
): number => {
  let count = 0;
  let current = fromMs;
  // from itself counts as day 1 (inclusive) — matches backend validation
  const fromDate = new Date(current);
  if (!isWeekendFn(fromDate) && !isHolidayFn?.(fromDate)) count++;
  while (count < days) {
    current += MS_PER_DAY;
    const d = new Date(current);
    if (!isWeekendFn(d) && !isHolidayFn?.(d)) count++;
  }
  return current;
};

const countWorkingDays = (
  fromMs: number,
  toMs: number,
  isWeekendFn: (_date: Date) => boolean,
  isHolidayFn?: (_date: Date) => boolean
): number => {
  let count = 0;
  // from inclusive — matches backend validation (both from and to count)
  let current = fromMs;
  while (current <= toMs) {
    const d = new Date(current);
    if (!isWeekendFn(d) && !isHolidayFn?.(d)) count++;
    current += MS_PER_DAY;
  }
  return count;
};

export const normalizeDate = (dateStr: string): number => {
  if (!dateStr) return 0;
  const parts = dateStr.split('/');
  if (parts.length !== 3) return 0;
  const day = parseInt(parts[0] || '0', 10);
  const month = parseInt(parts[1] || '0', 10) - 1;
  const year = parseInt(parts[2] || '0', 10);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return 0;
  const date = new Date(year, month, day);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
};

export const createTravelDateSchema = (
  t: (_key: string, _options?: Record<string, unknown>) => string,
  locale = 'ar-SA',
  isHolidayFn?: (_date: Date) => boolean
) => {
  const isWeekendFn = createIsWeekend(locale);
  return z
    .object({
      fromDate: z
        .string()
        .min(1, t('addTravelDateModal.validation.fromRequired')),
      toDate: z.string().min(1, t('addTravelDateModal.validation.toRequired')),
    })
    .superRefine((data, ctx) => {
      if (!data.fromDate || !data.toDate) return;

      const fromTime = normalizeDate(data.fromDate);
      const toTime = normalizeDate(data.toDate);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (fromTime < today.getTime()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fromDate'],
          message: t('addTravelDateModal.validation.fromFuture'),
        });
      }

      if (toTime <= fromTime) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['toDate'],
          message: t('addTravelDateModal.validation.toAfterFrom'),
        });
      } else {
        const durationDays = countWorkingDays(
          fromTime,
          toTime,
          isWeekendFn,
          isHolidayFn
        );
        if (durationDays < MIN_STAY_DAYS) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['toDate'],
            message: t('addTravelDateModal.validation.minStayDays', {
              days: MIN_STAY_DAYS,
            }),
          });
        }
      }
    });
};

export type TravelDateFormData = z.infer<
  ReturnType<typeof createTravelDateSchema>
>;
